import { StyledMUIButtonLink } from '@components/Button';
import { ThinDivider } from '@components/Product/Desktop/Panels/PartsCatalogue/styles';
import { spacing } from '@constants/spacing';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled, Typography } from '@mui/material';
import type { PartNode } from '@partful/shared-types';
import { useProduct } from '@providers/Product';
import { getKitsRelatedToPart } from '@services/kits/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface PartInKitProps {
  currentPart: PartNode;
  divider?: boolean;
}

export function PartInKit({ currentPart, divider }: Readonly<PartInKitProps>) {
  const { kits, productId } = useProduct();
  const matchedKitUrls = useMemo(
    () => getKitsRelatedToPart(currentPart, kits, productId),
    [currentPart, kits, productId]
  );
  const { t } = useTranslation('partsCatalogue');

  if (!matchedKitUrls || matchedKitUrls.length === 0) {
    return null;
  }

  return (
    <StyledPartInKitContainer>
      {divider && <ThinDivider />}
      <Typography variant="body1" sx={{ marginTop: spacing.mediumSpacing }}>
        {t('kitSection.partIncludedInKit')}
      </Typography>
      {matchedKitUrls.map((kitWithUrl) => (
        <StyledComponentPartContainer key={kitWithUrl.kit.externalId}>
          <StyledComponentDetailContainer>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {kitWithUrl.kit.name}
            </Typography>
            <Typography variant="body2">#{kitWithUrl.kit.externalId}</Typography>
          </StyledComponentDetailContainer>
          <StyledMUIButtonLink endIcon={<FontAwesomeIcon icon={faChevronRight} />} to={kitWithUrl.navUrl}>
            {t('kitSection.viewKit')}
          </StyledMUIButtonLink>
        </StyledComponentPartContainer>
      ))}
    </StyledPartInKitContainer>
  );
}

export const StyledPartInKitContainer = styled('div')`
  margin-right: ${spacing.mediumSpacing};

  // Properties that change on different breakpoints
  margin-left: ${spacing.mediumSpacing};

  // Desktop
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: 0;
  }
`;

export const StyledComponentDetailContainer = styled('div')`
  flex: 1;
`;

export const StyledComponentPartContainer = styled('div')`
  // Common to any breakpoint
  margin-top: ${spacing.mediumSpacing};
  margin-bottom: ${spacing.mediumSpacing};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
